import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, fmtDateTime } from "@/lib/checkins";
import { AlertRow } from "./AlertRow";
import { DomainResults } from "./DomainResults";
import { DiarySubmission } from "./DiarySubmission";
import { FeedbackControls } from "./FeedbackControls";
import { errMsg } from "./ResolveDialog";

function Answers({ sub }) {
  return (
    <div className="space-y-3" data-testid="checkin-answers">
      {sub.sections.map((s) => {
        const rows = sub.answers_display.filter((a) => a.section === s.code);
        if (!rows.length) return null;
        return (
          <div key={s.code}>
            <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{s.label}</div>
            <dl className="mt-1 divide-y divide-zinc-100 text-sm">
              {rows.map((a) => (
                <div key={a.code} className="grid grid-cols-[1fr_auto] gap-3 py-1.5">
                  <dt className="text-zinc-700"><span className="font-mono-num text-zinc-400">{a.code}</span> {a.prompt}</dt>
                  <dd className="text-right font-medium text-zinc-950">{a.display}</dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </div>
  );
}

function Notes({ sub, readOnly, onChanged }) {
  const [text, setText] = useState("");
  async function add() {
    try {
      await api.post(`/checkin-submissions/${sub.id}/notes`, { text });
      setText("");
      onChanged();
    } catch (e) {
      toast.error("Couldn't add note", { description: errMsg(e) });
    }
  }
  return (
    <div className="space-y-2" data-testid="checkin-notes">
      {sub.notes.map((n) => <p key={n.id} className="rounded bg-zinc-50 p-2 text-sm"><span className="text-xs text-zinc-500">{fmtDateTime(n.at)} · {n.by_name}</span><br />{n.text}</p>)}
      {!readOnly && (
        <div className="flex gap-2">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Practitioner note / action taken" data-testid="checkin-note-input" />
          <Button onClick={add} disabled={!text.trim()} data-testid="checkin-note-add">Add</Button>
        </div>
      )}
    </div>
  );
}

const H = ({ children }) => <h3 className="mt-5 mb-2 font-display text-base font-semibold text-zinc-950">{children}</h3>;

export function SubmissionDialog({ sid, staff, onClose, onChanged }) {
  const { user } = useAuth();
  const readOnly = user?.role === "auditor";
  const [sub, setSub] = useState(null);
  const load = useCallback(() => {
    api.get(`/checkin-submissions/${sid}`).then((r) => setSub(r.data)).catch((e) => toast.error("Load failed", { description: errMsg(e) }));
  }, [sid]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="checkin-submission-dialog">
        <DialogHeader><DialogTitle>{sub ? `${sub.template_name} — ${sub.client_name}` : "Progress review"}</DialogTitle></DialogHeader>
        {!sub ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : sub.kind === "diary" ? (
          <DiarySubmission sub={sub} staff={staff} readOnly={readOnly} onChanged={refresh} notes={<Notes sub={sub} readOnly={readOnly} onChanged={refresh} />} />
        ) : (
          <div>
            <p className="text-sm text-zinc-600" data-testid="checkin-submission-meta">
              Week {fmtDate(sub.period?.start)} – {fmtDate(sub.period?.end)} · submitted {fmtDateTime(sub.submitted_at)} · v{sub.semantic_version} · {sub.source === "assisted" ? "practitioner-assisted" : "client via email link"}
              {sub.typical_week !== "yes" && <span className="ml-2 rounded bg-zinc-200 px-1.5 py-0.5 text-xs font-semibold text-zinc-800">Non-typical week</span>}
            </p>
            {sub.safety?.length > 0 && (
              <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900" data-testid="checkin-safety-flags">
                <strong>Safety flags ({sub.safety_severity}):</strong> {sub.safety.map((s) => s.label).join("; ")}. Progression advice suppressed.
              </div>
            )}
            <div className="mt-3"><FeedbackControls sub={sub} readOnly={readOnly} onChanged={refresh} /></div>
            <H>Results</H>
            <DomainResults sub={sub} readOnly={readOnly} onChanged={refresh} />
            {sub.alerts.length > 0 && <><H>Alerts & tasks</H><div className="space-y-2">{sub.alerts.map((a) => <AlertRow key={a.id} alert={a} staff={staff} onChanged={refresh} readOnly={readOnly} showClient={false} />)}</div></>}
            <H>Answers</H>
            <Answers sub={sub} />
            <H>Notes</H>
            <Notes sub={sub} readOnly={readOnly} onChanged={refresh} />
            <p className="mt-4 text-2xs text-zinc-400">{sub.evidence_note}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
